import {
  MODULES,
  MODULE_KEYS,
  can,
  isStaffSurface,
  seatsUsed,
  type AccessReviewStatus,
  type ApprovalRequest,
  type AuditEntry,
  type Group,
  type Role,
  type Tenant,
  type TenantSso,
  type TenantUser,
} from '@titan/shared';
import { useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../../session/SessionProvider.js';
import { Loading, ScreenBody, ScreenHeader } from '../../shell/Shell.js';
import { Bullets, Card, InlineError, Muted, Pill, PillRow, TwoColumn } from '../../ui/index.js';
import { AccessCenter } from '../access/AccessCenter.js';
import { useLoad } from '../useLoad.js';

interface OrgResponse {
  tenant: Pick<Tenant, 'id' | 'name' | 'plan' | 'seats' | 'modules' | 'allowedDomains' | 'sso' | 'mfa' | 'status' | 'csm'> & { ssoProvider: TenantSso | null };
  users: TenantUser[];
  groups: Group[];
  roles: Role[];
  audit: AuditEntry[];
  approvals: ApprovalRequest[];
  accessReview: AccessReviewStatus;
}

/**
 * Spec 07 client-side view. The organization's own identity management, through the
 * shared access center. Staff see it as a preview or under impersonation.
 */
export function Organization() {
  const { me, exitImpersonation } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const staff = me ? isStaffSurface(me.actor) : false;
  const staffTenantId = staff ? params.get('tenantId') : null;
  const path = staff ? (staffTenantId ? `/org?tenantId=${encodeURIComponent(staffTenantId)}` : null) : '/org';
  const { data, error, loading, reload } = useLoad<OrgResponse>(path);

  if (!me) return null;
  if (staff && !staffTenantId) {
    navigate('/admin/tenants', { replace: true });
    return null;
  }
  if (loading && !data) return <Loading />;
  if (!data) {
    return (
      <ScreenBody>
        <InlineError>{error ?? 'Not found.'}</InlineError>
      </ScreenBody>
    );
  }

  const { tenant, users, groups, roles, audit, approvals, accessReview } = data;
  const impersonating = me.actor.kind === 'staff' && Boolean(me.actor.impersonating);
  const canWrite = can(me.actor, 'management') === 'full';
  const showBadge = staff || impersonating;
  const enabled = MODULE_KEYS.filter((k) => tenant.modules[k]);

  const back = impersonating
    ? {
        label: 'Exit impersonation',
        onClick: () => {
          void exitImpersonation().then((tenantId) => navigate(tenantId ? `/admin/tenants/${tenantId}` : '/admin/tenants'));
        },
      }
    : staff
      ? { label: 'Back to admin view', to: `/admin/tenants/${tenant.id}` }
      : undefined;

  return (
    <>
      <ScreenHeader
        eyebrow="administration · your organization"
        title="Your organization"
        description={`Manage the people at ${tenant.name} who use the hub. ${seatsUsed(users)} of ${tenant.seats} seats used.`}
        {...(back ? { back } : {})}
        titleAside={
          showBadge ? (
            <PillRow>
              <Pill tone="warning">Client-side view · this is what {tenant.name} sees</Pill>
            </PillRow>
          ) : undefined
        }
      />
      <ScreenBody narrow>
        <AccessCenter
          tenant={{ id: tenant.id, name: tenant.name, seats: tenant.seats, allowedDomains: tenant.allowedDomains, modules: tenant.modules, ssoLabel: tenant.ssoProvider?.label ?? null }}
          users={users}
          groups={groups}
          roles={roles}
          audit={audit}
          approvals={approvals}
          accessReview={accessReview}
          actor={me.actor}
          {...(staffTenantId ? { staffTenantId } : {})}
          canWrite={canWrite}
          onChanged={reload}
        />

        <TwoColumn>
          <Card title="What you can change, and what you cannot">
            <Bullets
              items={[
                `You manage your own people. Adding a seat beyond ${tenant.seats} goes through your CSM.`,
                'Entitlements are set in your contract. Ask DinoCloud to turn a module on or off.',
                'Giving someone management access, and appointing or removing a superadmin, waits for a second superadmin or DinoCloud to approve.',
                'DinoCloud staff access to your tenant is logged and visible in your audit trail.',
                tenant.ssoProvider
                  ? `Single sign-on is connected through ${tenant.ssoProvider.label}${tenant.ssoProvider.enforce ? '; passwords are disabled' : ''}. DinoCloud changes this.`
                  : 'Single sign-on is not configured yet. Everyone signs in with email, password and an authenticator code. Ask DinoCloud to connect your identity provider.',
                `Sign-in is limited to ${tenant.allowedDomains.join(', ')} addresses. DinoCloud changes this list.`,
              ]}
            />
          </Card>
          <Card title="Modules you have" description="Set by your contract">
            <PillRow>
              {enabled.length === 0 && <Muted>No modules enabled yet.</Muted>}
              {enabled.map((k) => (
                <Pill key={k} tone="positive">
                  {MODULES[k].label}
                </Pill>
              ))}
            </PillRow>
          </Card>
        </TwoColumn>
      </ScreenBody>
    </>
  );
}
